import { api } from './api';

export interface ScheduleNotification {
  id: string;
  organizationId: string;
  parentId: string;
  matchId?: string | null;
  globalMatchId?: string | null;
  playerId?: string | null;
  conflictId?: string | null;
  type: ScheduleNotificationType;
  title: string;
  message: string;
  changeDetails?: Record<string, any> | null;
  read: boolean;
  readAt?: string | null;
  emailSent: boolean;
  pushSent: boolean;
  createdAt: string;
  match?: {
    id: string;
    homeTeam: string;
    awayTeam: string;
    dateTime?: string | null;
    location?: string | null;
  } | null;
  player?: {
    id: string;
    name: string;
  } | null;
}

export type ScheduleNotificationType =
  | 'MATCH_DATE_CHANGED'
  | 'MATCH_TIME_CHANGED'
  | 'MATCH_LOCATION_CHANGED'
  | 'MATCH_NOW_SCHEDULED'
  | 'MATCH_NOW_PENDING'
  | 'CONFLICT_DETECTED'
  | 'CONFLICT_RESOLVED'
  | 'NEW_MATCH_ADDED'
  | 'SYNC_COMPLETE'
  | 'SCORE_POSTED';

export interface NotificationPage {
  notifications: ScheduleNotification[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface NotificationPreference {
  id: string;
  parentId: string;
  emailEnabled: boolean;
  emailDigestMode: 'immediate' | 'daily' | 'weekly';
  emailDigestHour: number;
  pushEnabled: boolean;
  emailOnDateChange: boolean;
  emailOnTimeChange: boolean;
  emailOnLocationChange: boolean;
  emailOnStatusChange: boolean;
  emailOnConflict: boolean;
  emailOnNewMatch: boolean;
  emailOnSyncComplete: boolean;
  emailOnScorePosted: boolean;
  pushOnDateChange: boolean;
  pushOnTimeChange: boolean;
  pushOnLocationChange: boolean;
  pushOnStatusChange: boolean;
  pushOnConflict: boolean;
  pushOnNewMatch: boolean;
  pushOnSyncComplete: boolean;
  pushOnScorePosted: boolean;
  customized: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PushSubscriptionInfo {
  id: string;
  endpoint: string;
  deviceName?: string | null;
  createdAt: string;
  lastUsedAt: string;
}

/**
 * Get paginated notifications for current user
 */
export async function getNotifications(
  page = 1,
  pageSize = 20,
  unreadOnly = false
): Promise<NotificationPage> {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (unreadOnly) params.set('unreadOnly', 'true');

  const response = await api.get<NotificationPage>(`/notifications?${params}`);
  return response.data;
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<number> {
  const response = await api.get<{ unreadCount: number }>('/notifications/unread-count');
  return response.data.unreadCount;
}

/**
 * Mark specific notifications as read
 */
export async function markAsRead(notificationIds: string[]): Promise<void> {
  await api.post('/notifications/mark-read', { notificationIds });
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ markedCount: number }> {
  const response = await api.post<{ success: boolean; markedCount: number }>('/notifications/mark-all-read');
  return response.data;
}

/**
 * Delete a specific notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await api.delete(`/notifications/${notificationId}`);
}

/**
 * Delete all notifications
 */
export async function clearAllNotifications(): Promise<{ deletedCount: number }> {
  const response = await api.post<{ success: boolean; deletedCount: number }>('/notifications/clear-all');
  return response.data;
}

/**
 * Get notification preferences
 */
export async function getNotificationPreferences(): Promise<NotificationPreference> {
  const response = await api.get<NotificationPreference>('/notifications/preferences');
  return response.data;
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  updates: Partial<Omit<NotificationPreference, 'id' | 'parentId' | 'createdAt' | 'updatedAt'>>
): Promise<NotificationPreference> {
  const response = await api.put<NotificationPreference>('/notifications/preferences', updates);
  return response.data;
}

/**
 * Get VAPID public key for push subscriptions
 */
export async function getVapidPublicKey(): Promise<{ publicKey: string | null; enabled: boolean }> {
  const response = await api.get<{ publicKey: string | null; enabled: boolean }>('/push-subscriptions/vapid-key');
  return response.data;
}

/**
 * Get user's registered push subscriptions
 */
export async function getPushSubscriptions(): Promise<PushSubscriptionInfo[]> {
  const response = await api.get<{ subscriptions: PushSubscriptionInfo[] }>('/push-subscriptions');
  return response.data.subscriptions;
}

/**
 * Register a new push subscription
 */
export async function registerPushSubscription(
  subscription: PushSubscriptionJSON,
  deviceName?: string
): Promise<PushSubscriptionInfo> {
  const response = await api.post<PushSubscriptionInfo>('/push-subscriptions', {
    endpoint: subscription.endpoint,
    keys: subscription.keys,
    deviceName,
  });
  return response.data;
}

/**
 * Remove a push subscription by ID
 */
export async function removePushSubscription(id: string): Promise<void> {
  await api.delete(`/push-subscriptions/${id}`);
}

/**
 * Rename a push subscription device
 */
export async function renamePushSubscription(id: string, deviceName: string): Promise<void> {
  await api.patch(`/push-subscriptions/${id}`, { deviceName });
}

/**
 * Send a test push notification
 */
export async function sendTestNotification(): Promise<void> {
  await api.post('/push-subscriptions/test');
}
